import type { Sprint, Task } from '@nexo/types';
import { Button, EmptyState, Textarea, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ChevronRight, MessageSquareQuote } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { formatLongDate, formatNumber } from '../../lib/format';
import { can } from '../../lib/permissions';
import { keys } from '../../lib/queries';
import { TaskRow } from '../tasks/TaskRow';
import { VelocityChart } from './SprintCharts';
import { averageVelocity, sprintDates } from './sprintUtils';
import { errorMessage } from '../../lib/forms';

/** The «Informes» tab: velocity over time and the report of every completed sprint. */
export function SprintReports({ sprints, tasks }: { sprints: Sprint[]; tasks: Task[] }) {
  const { t } = useTranslation();
  const completed = sprints
    .filter((s) => s.status === 'completed')
    .sort((a, b) => (a.completedAt ?? '').localeCompare(b.completedAt ?? ''));
  const average = averageVelocity(completed);
  const withResult = completed.filter((s) => s.committed && s.result);
  const committedTotal = withResult.reduce((sum, s) => sum + (s.committed?.points ?? 0), 0);
  const doneTotal = withResult.reduce((sum, s) => sum + (s.result?.points ?? 0), 0);

  if (completed.length === 0) {
    return (
      <EmptyState
        icon={<BarChart3 className="size-8" />}
        title={t('Aún no hay sprints completados')}
        description={t(
          'Cuando completes el primero verás aquí la velocidad del equipo y el informe de cada sprint.',
        )}
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 px-3 py-4 sm:px-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Figure
          label={t('Velocidad media')}
          value={average === null ? '—' : t('{{points}} pts', { points: average })}
          hint={t('Últimos 3 sprints completados')}
        />
        <Figure
          label={t('Fiabilidad')}
          value={
            committedTotal > 0
              ? formatNumber(doneTotal / committedTotal, { style: 'percent' })
              : '—'
          }
          hint={t('Puntos completados sobre comprometidos')}
        />
        <Figure label={t('Sprints completados')} value={String(completed.length)} />
      </div>
      <VelocityChart sprints={completed.slice(-10)} />
      <section>
        <h3 className="mb-2 text-sm font-semibold">{t('Historial')}</h3>
        <ul className="flex flex-col gap-2">
          {[...completed].reverse().map((sprint) => (
            <SprintReport
              key={sprint.id}
              sprint={sprint}
              done={tasks.filter((task) => task.sprintId === sprint.id && task.status === 'done')}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface px-4 py-3 shadow-panel">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-0.5 text-2xl font-semibold tracking-tight">{value}</p>
      {hint && <p className="text-xs text-subtle">{hint}</p>}
    </div>
  );
}

function SprintReport({ sprint, done }: { sprint: Sprint; done: Task[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { committed, result } = sprint;
  return (
    <li className="rounded-xl border border-border bg-surface">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left"
      >
        <ChevronRight
          className={cn('size-4 shrink-0 text-subtle transition-transform', open && 'rotate-90')}
        />
        <span className="font-medium">{sprint.name}</span>
        <span className="text-xs text-muted">{sprintDates(sprint)}</span>
        <span className="ml-auto text-xs text-muted tabular-nums">
          {result
            ? [
                committed
                  ? t('{{done}} de {{total}} pts', { done: result.points, total: committed.points })
                  : t('{{points}} pts', { points: result.points }),
                result.tasks === 1 ? t('1 tarea') : t('{{count}} tareas', { count: result.tasks }),
                result.carriedOver
                  ? result.carriedOver === 1
                    ? t('1 pasó al siguiente')
                    : t('{{count}} pasaron al siguiente', { count: result.carriedOver })
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : t('Sin datos del cierre')}
        </span>
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3">
          {sprint.goal && (
            <p className="mb-2 text-sm">
              <span className="text-muted">{t('Objetivo')}: </span>
              {sprint.goal}
            </p>
          )}
          {sprint.completedAt && (
            <p className="mb-3 text-xs text-subtle">
              {t('Completado el {{date}}', { date: formatLongDate(sprint.completedAt) })}
            </p>
          )}
          <Review sprint={sprint} />
          <h4 className="mt-4 mb-1 text-xs font-semibold tracking-wider text-subtle uppercase">
            {t('Tareas completadas ({{count}})', { count: done.length })}
          </h4>
          {done.length === 0 ? (
            <p className="text-sm text-muted">{t('No se completó ninguna tarea.')}</p>
          ) : (
            <div className="flex flex-col">
              {done.map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** Retrospective notes: shown to everyone, editable with «Gestionar tareas». */
function Review({ sprint }: { sprint: Sprint }) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(sprint.review ?? '');
  const save = useMutation({
    mutationFn: () => api.sprints.update(sprint.id, { review: text.trim() || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.sprints(team.id) });
      setEditing(false);
    },
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });

  if (editing) {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
        className="flex flex-col gap-2"
      >
        <Textarea
          aria-label={t('Retrospectiva')}
          autoFocus
          rows={4}
          maxLength={5000}
          value={text}
          placeholder={t('Qué ha ido bien, qué mejorar en el siguiente…')}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            {t('Cancelar')}
          </Button>
          <Button size="sm" type="submit" loading={save.isPending}>
            {t('Guardar')}
          </Button>
        </div>
      </form>
    );
  }
  return (
    <div className="rounded-lg bg-surface-2 px-3 py-2.5">
      <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted">
        <MessageSquareQuote className="size-3.5" /> {t('Retrospectiva')}
        {can(team, 'manage_tasks') && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="ml-auto font-normal text-accent hover:underline"
          >
            {sprint.review ? t('Editar') : t('Escribir')}
          </button>
        )}
      </p>
      <p className="text-sm whitespace-pre-line text-text">
        {sprint.review ?? <span className="text-subtle">{t('Sin notas.')}</span>}
      </p>
    </div>
  );
}
